import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Camera, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import {
  getAll,
  create,
  update,
  remove,
} from "../../services/firestoreService";
import { updateCollectionVersion } from "../../services/versionService";
import { SEED_PORTFOLIO } from "../../data/seed";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import ImagePicker from "./ImagePicker";
import SeedDataButton from "./SeedDataButton";

const COLLECTION = "portfolios";
const EMPTY_FORM = { title: "", category: "", imageRef: null };

export default function PortfolioEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAll(COLLECTION);
      setItems(data);
    } catch {
      toast.error("Gagal memuat portfolio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      category: item.category || "",
      imageRef: item.imageRef || null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Judul wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await update(COLLECTION, editing.id, form);
        toast.success("Portfolio berhasil diperbarui!");
      } else {
        await create(COLLECTION, form);
        toast.success("Portfolio berhasil dibuat!");
      }
      await updateCollectionVersion(COLLECTION);
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(COLLECTION, deleteTarget.id);
      toast.success("Portfolio berhasil dihapus!");
      await updateCollectionVersion(COLLECTION);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
            <h1 className="text-2xl font-display text-[#fdfbf7]">Portfolio</h1>
          </div>
          <p className="text-sm text-[#a6a196] ml-4">
            Kelola galeri portfolio makeup
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SeedDataButton
            collectionName={COLLECTION}
            data={SEED_PORTFOLIO}
            onComplete={loadItems}
            isEmpty={items.length === 0}
          />
          <Button
            onClick={openCreate}
            className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#a6a196] border border-dashed border-[#d4af37]/20 rounded-lg">
          <Camera className="w-12 h-12 mb-3 opacity-20" />
          <p className="mb-4 text-sm">Belum ada item portfolio</p>
          <Button
            onClick={openCreate}
            variant="outline"
            className="border-[#d4af37]/30 text-[#d4af37]"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah Item
          </Button>
        </div>
      ) : (
        <>
          {/* Mobile: Card view */}
          <div className="block space-y-3 md:hidden">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-[#15120f] border border-[#d4af37]/10 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-md overflow-hidden border border-[#d4af37]/10 bg-[#15120f] shrink-0">
                        {item.imageRef?.url ? (
                          <img
                            src={item.imageRef.url}
                            alt={item.title}
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-full h-full">
                            <Camera className="w-4 h-4 text-[#a6a196]/30" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-[#fdfbf7] font-medium truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-[#a6a196] truncate">
                          {item.category}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(item)}
                      className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(item)}
                      className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table view */}
          <div className="hidden md:block rounded-lg border border-[#d4af37]/10 overflow-hidden bg-[#0a0908]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#d4af37]/10">
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Image
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Title
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Category
                    </th>
                    <th className="text-right text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#d4af37]/5">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-[#15120f]/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="w-12 h-12 rounded-md overflow-hidden border border-[#d4af37]/10 bg-[#15120f]">
                          {item.imageRef?.url ? (
                            <img
                              src={item.imageRef.url}
                              alt={item.title}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full">
                              <Camera className="w-4 h-4 text-[#a6a196]/30" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#fdfbf7] font-medium">
                          {item.title}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#a6a196]">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(item)}
                            className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(item)}
                            className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#fdfbf7]">
              {editing ? "Edit Portfolio Item" : "Tambah Portfolio Item"}
            </DialogTitle>
            <DialogDescription className="text-[#a6a196]">
              {editing
                ? "Ubah detail item portfolio"
                : "Isi detail item portfolio baru"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-[#a6a196]">Title</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Judul portfolio"
                className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#a6a196]">Kategori</label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Kategori (e.g. Adat Jawa Solo)"
                className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
              />
            </div>
            <ImagePicker
              value={form.imageRef}
              onChange={(img) => setForm({ ...form, imageRef: img })}
              label="Image"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#d4af37]/20 text-[#a6a196]"
            >
              Batal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Menyimpan...
                </>
              ) : editing ? (
                "Perbarui"
              ) : (
                "Buat"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#fdfbf7]">
              Hapus Portfolio Item
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a6a196]">
              Apakah Anda yakin ingin menghapus "{deleteTarget?.title}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0a0908] border-[#d4af37]/20 text-[#a6a196] hover:bg-[#15120f]">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="text-red-400 border bg-red-500/10 border-red-500/20 hover:bg-red-500/20"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
